<template>
    <div
        class="container-fluid w-100 h-100"
        style="overflow-x: hidden"
    >
        <navbar
            :title="$t('contracts.title')"
            class="mb-1"
            :show-back="contract !== null"
            @back="goBack"
        />


        <div
            ref="container"
            class="col-sm-12 nopads pt-1"
        >
            <TransitionGroup name="fade">
                <div key="search-contract-list">
                    <keep-alive>
                        <contract-search
                            v-if="!editMode && !showWizard"
                            @search="fetchContracts"
                        />
                    </keep-alive>
                    <div
                        v-if="!editMode && !showWizard"
                        class="col-sm-12 my-2"
                    >
                        <div
                            class="data-list-item add"
                            @click.stop="addContract"
                        >
                            <div class="item-img-cont add-title">
                                <i class="fa fa-plus" />
                            </div>
                            <div class="item-text-cont">
                                <span class="item-title-add">{{ $t('common.add_new') }}</span>
                                <font-awesome-icon
                                    icon="file-contract"
                                    class="card-body-icon"
                                />
                            </div>
                        </div>
                    </div>
                    <keep-alive>
                        <contract-list
                            v-if="!editMode && !showWizard"
                            :results="results"
                            @edit="editContract"
                        />
                    </keep-alive>
                </div>
            </TransitionGroup>
            <Transition name="fade">
                <contract-wizard
                    v-if="showWizard"
                    :user="user"
                    @customer-set="setCustomer"
                    @contractor-set="setContractor"
                    @reset-parties="resetParties"
                />
            </Transition>
            <Transition name="fade">
                <contract-details
                    ref="details"
                    v-if="editMode"
                    v-model="contract"
                    :user="user"
                    :is-observer="isObserver"
                    :active-order-editor="activeOrderEditor"
                    @order-active-change="checkActiveOrder"
                    @show-list="showList"
                    @close-and-update="closeAndUpdate"
                    @scroll-top="scrollTop"
                />
            </Transition>
            <div
                v-if="loading"
                id="loader"
                class="spinner"
            />
        </div>
    </div>
</template>


<script>
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import {restApi} from '../mixins/RestApiMixin'
import ContractWizard from './ContractWizard'
import ContractList from './ContractList'
import ContractSearch from './ContractSearch'
import ContractDetails from "@/components/contract/ContractDetails";
import Navbar from "@/components/utils/Navbar";
import {inject} from "vue";

export default {
    name: 'Contracts',
    components: {Navbar, ContractDetails, ContractList, ContractWizard, ContractSearch},
    mixins: [workManagementHelper, restApi],
    props: {
        user: {
            type: Object,
            default: null
        },
        isObserver: {
            type: Boolean,
            default: false
        },
        preSelectedContract: {
            type: Object,
            default: null
        },
        preSelectedTab: {
            type: Number,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            maxContracts: 200,
            results: [],
            contract: null,
            editMode: false,
            showWizard: false,
            loading: false,
            lastSearchedParams: null,
            customerName: null,
            contractorName: null,
            activeOrderEditor: false,
            scrollPosition: 0
        }
    },
    mounted: function () {
        this.lastSearchedParams = {open: 1}
        if (this.preSelectedContract) {
            this.editContract(this.preSelectedContract)
            if (this.preSelectedTab) {
                this.$nextTick(() => this.$refs.details.showTab(this.preSelectedTab))
            }
        }
    },
    methods: {
        checkActiveOrder: function(active){
            this.activeOrderEditor = active
        },
        fetchContracts: function (params) {
            this.loading = true
            this.lastSearchedParams = params
            this.restFetchParams(this.contractUrl, this.lastSearchedParams, this.handleResponse)
        },

        handleResponse: function (response) {
            this.loading = false
            if (response && response.data) {
                this.results = response.data
                // Inform user if max number of contracts were returned
                if (this.results.length === this.maxContracts) {
                    this.showAlert(this.$t('contracts.external.max_returned'), 'warning')
                }
            }
        },

        editContract: function (contract) {
            this.contract = this.jsonToItem(contract);
            this.editMode = true
        },

        addContract: function () {
            this.contract = this.newContract()
            this.showWizard = true
        },

        showList: function () {
            this.editMode = false
            this.contract = null
            this.showWizard = false
        },

        closeAndUpdate: function () {
            if(this.activeOrderEditor){
                this.editMode = true
                this.activeOrderEditor = false
                this.$nextTick(function () {
                    this.$refs.container.scroll({
                        top: this.scrollPosition,
                        behavior: 'smooth',
                    });
                })
            } else {
                this.fetchContracts(this.lastSearchedParams)
                this.showList()
            }
        },

        goBack: function () {
            this.fetchContracts(this.lastSearchedParams)
            this.showList()
        },

        setCustomer: function (customer) {
            this.contract.customer = customer
            this.customerName = customer.name
            this.editMode = (this.contract.customer && this.contract.customer.id && this.contract.contractor && this.contract.contractor.id)
            this.showWizard = !this.editMode
        },

        setContractor: function (contractor) {
            this.contract.contractor = contractor
            this.contractorName = contractor.name
            this.editMode = (this.contract.customer && this.contract.customer.id && this.contract.contractor && this.contract.contractor.id)
            this.showWizard = !this.editMode
        },

        resetParties: function () {
            // Stepping back to the role question clears any party already chosen,
            // so a stale customer/contractor can't satisfy the both-sides check.
            if (this.contract) {
                this.contract.customer = -1
                this.contract.contractor = -1
            }
            this.customerName = null
            this.contractorName = null
            this.editMode = false
        },

        scrollTop: function () {
            this.$nextTick(function () {
                this.$refs.container.scrollTo({ top: 0, behavior: 'smooth' })
            })
        }
    }
}
</script>
